// ============================================================
// quiz-v2-config.js
// Vitable Quiz v2 — complete question configuration
//
// Source of truth for all quiz questions, answer options, ordering,
// conditional criteria, and validation rules.
//
// Customer-facing text: from Octane quiz export (authoritative)
// Option shorts/values: from Octane mapping CSV (backend contract)
// Question ordering:    from legacy quiz CSV (float values)
// Criteria:            confirmed per project spec
// Section screen content: confirmed from Octane quiz builder
// ============================================================

const QUIZ_CONFIG = {
  questions: [

    // ─── PRE-QUIZ INTRO SCREENS ────────────────────────────────────────────────
    // These two screens appear before any questions, replicating the existing
    // Octane intro experience. All content is confirmed and final.

    {
      short: 'welcome',
      text: 'Good Nutrition is <i>Personal</i>',
      subtext: "We help you find the right vitamins, recommended just for you. To get started we'll ask you some questions about your goals, lifestyle and diet. So we can help you feel good like you should.",
      image: 'https://cdn.shopify.com/s/files/1/0914/5070/4171/files/Star_Rating_Desktop.png?v=1754868523',
      badge: {
        icon: 'https://cdn.shopify.com/s/files/1/0914/5070/4171/files/clock-circle.svg?v=1755610482',
        text: 'Takes 2 minutes',
      },
      cta: "Let's Get Started",
      type: 'section',
      order: 0,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
    },
    {
      // The [Data Privacy Policy] token in subtext must be rendered as a link
      // pointing to privacyPolicyUrl. All other text renders as plain paragraphs.
      short: 'disclaimer',
      text: 'Before we start...',
      subtext: "All your answers are confidential!\nRead our [Data Privacy Policy].\nThis service does not provide medical advice and is not intended to diagnose, treat, cure or prevent any disease. Some medicines may not be right for you. Read the label and any warnings before purchase. If you have any pre-existing conditions, or are on any medications, always talk to your health professional before use.",
      privacyPolicyUrl: 'https://vitable.com.au/policies/privacy-policy',
      cta: 'Continue',
      type: 'section',
      order: 0.5,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
    },

    // ─── ABOUT YOU ─────────────────────────────────────────────────────────────

    {
      short: 'first_name',
      text: "What's your first name (or nickname)?",
      description: 'We will use this to personalise your experience.',
      type: 'text-input',
      order: 1,
      skipForLoggedIn: true,
      criteria: [],
      criteriaIsAnd: false,
      validation: { minLength: 1, maxLength: 20, pattern: /^[a-zA-Z0-9 '\-]+$/ },
    },
    {
      // {first_name} token is replaced at render time.
      // No CTA — auto-transitions like other transition screens.
      // start_quiz analytics fires when this screen renders (in the renderer).
      short: 'get_started',
      text: 'Nice to meet you, {first_name}!',
      type: 'section',
      order: 2,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
    },
    {
      short: 'supplement_perception',
      text: 'When it comes to vitamins and supplements, you:',
      type: 'single-choice',
      order: 2.5,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      options: [
        { short: 'informed',  text: 'Know more than the average person' },
        { short: 'curious',   text: 'Know a bit, but want to learn more' },
        { short: 'skeptical', text: 'Are not convinced yet' },
      ],
    },
    {
      short: 'taken_in_the_past',
      text: 'Have you taken vitamins and/or supplements in the past?',
      type: 'single-choice',
      order: 3,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      options: [
        { short: 'always',     text: 'For a while' },
        { short: 'occasional', text: 'On and off' },
        { short: 'never',      text: 'Never' },
      ],
    },
    {
      short: 'currently_take',
      text: 'How many vitamins or supplements do you currently take?',
      type: 'single-choice',
      order: 4,
      skipForLoggedIn: false,
      criteria: [{ question: 'taken_in_the_past', options: ['always', 'occasional'] }],
      criteriaIsAnd: false,
      // '46113' was an Excel date corruption in the Octane mapping CSV export.
      // The actual Octane property value is '1-4'. Confirmed correct.
      options: [
        { short: 'none', text: 'None' },
        { short: '1-4',  text: '1 to 4' },
        { short: '5',    text: 'More than 5' },
      ],
    },

    // ─── TRANSITION SCREEN ─────────────────────────────────────────────────────
    // Transition screens display a 4-step progress indicator (Basics / Goals /
    // Diet / Lifestyle) with the current step circled. They auto-advance after
    // a short timer — no user interaction required.
    // The renderer uses the `text` field to determine which step to highlight.
    // Steps array is always: ['Basics', 'Goals', 'Diet', 'Lifestyle']

    {
      short: 'basics',
      text: 'Basics',
      type: 'section',
      order: 5,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
    },

    // ─── BASICS ────────────────────────────────────────────────────────────────

    {
      short: 'gender',
      text: 'What sex were you assigned at birth?',
      description: 'We appreciate this might not align with your gender identity.',
      type: 'single-choice',
      order: 6,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      options: [
        { short: 'male',   text: 'Male' },
        { short: 'female', text: 'Female' },
        { short: 'none',   text: 'Prefer not to say' },
      ],
    },
    {
      // Shown when gender = female OR none (prefer not to say treated same as female)
      short: 'pregnancy',
      text: 'Are you currently trying for a baby, pregnant or have you recently given birth?',
      type: 'single-choice',
      order: 10,
      skipForLoggedIn: false,
      criteria: [{ question: 'gender', options: ['female', 'none'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'trying',        text: "I'm trying for a baby" },
        { short: 'pregnant',      text: "I'm pregnant" },
        { short: 'breastfeeding', text: "I'm breastfeeding" },
        { short: 'no',            text: 'None of the above' },
      ],
    },
    {
      // Shown when gender = female OR none (same criteria as pregnancy)
      short: 'natal',
      text: 'Are you interested in prenatal or postnatal health?',
      type: 'single-choice',
      order: 10.2,
      skipForLoggedIn: false,
      criteria: [{ question: 'gender', options: ['female', 'none'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'yes', text: 'Yes' },
        { short: 'no',  text: 'No' },
      ],
    },
    {
      // number-input replaces Octane dropdown — submitted as string e.g. "32"
      short: 'age',
      text: 'How old are you?',
      type: 'number-input',
      order: 10.5,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      validation: { min: 18, max: 100 },
    },

    // ─── TRANSITION SCREEN ─────────────────────────────────────────────────────

    {
      short: 'goals',
      text: 'Goals',
      type: 'section',
      order: 11,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
    },

    // ─── HEALTH GOALS ──────────────────────────────────────────────────────────

    {
      // ranked-multi-select: user selects up to 6 areas in priority order (maxRank: 6).
      // Submitted as comma-separated ordered string: "energy,sleep,stress"
      // Position = rank; backend multipliers use this ordering.
      short: 'improve_area',
      text: 'Which areas of your health are you looking to improve?',
      description: 'Select up to six goals that you\u2019d like to focus on.',
      type: 'ranked-multi-select',
      order: 12,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      maxSelections: 6,
      maxRank: 6,
      twoColumn: true,
      options: [
        { short: 'immunity',  text: 'Immunity',  icon: 'https://cdn.shopify.com/s/files/1/0914/5070/4171/files/icon_immunity.svg?v=1752737320' },
        { short: 'hair',      text: 'Hair',      icon: 'https://cdn.shopify.com/s/files/1/0914/5070/4171/files/icon_hair.svg?v=1752737976' },
        { short: 'digestion', text: 'Digestion', icon: 'https://cdn.shopify.com/s/files/1/0914/5070/4171/files/icon_digestion.svg?v=1752737988' },
        { short: 'stress',    text: 'Stress',    icon: 'https://cdn.shopify.com/s/files/1/0914/5070/4171/files/icon_stress.svg?v=1752737955' },
        { short: 'brain',     text: 'Brain',     icon: 'https://cdn.shopify.com/s/files/1/0914/5070/4171/files/icon_brain.svg?v=1752737937' },
        { short: 'energy',    text: 'Energy',    icon: 'https://cdn.shopify.com/s/files/1/0914/5070/4171/files/icon_energy.svg?v=1752737942' },
        { short: 'sleep',     text: 'Sleep',     icon: 'https://cdn.shopify.com/s/files/1/0914/5070/4171/files/icon_sleep.svg?v=1752737948' },
        { short: 'heart',     text: 'Heart',     icon: 'https://cdn.shopify.com/s/files/1/0914/5070/4171/files/icon_heart.svg?v=1752737929' },
        { short: 'skin',      text: 'Skin',      icon: 'https://cdn.shopify.com/s/files/1/0914/5070/4171/files/icon_skin.svg?v=1752737963' },
        { short: 'joints',    text: 'Joints',    icon: 'https://cdn.shopify.com/s/files/1/0914/5070/4171/files/icon_joints.svg?v=1752737981' },
        { short: 'bones',     text: 'Bones',     icon: 'https://cdn.shopify.com/s/files/1/0914/5070/4171/files/icon_bones.svg?v=1752737994' },
        { short: 'fitness',   text: 'Fitness',   icon: 'https://cdn.shopify.com/s/files/1/0914/5070/4171/files/Lifestyle_Icon_c1ae5a70-f519-4f50-b1d5-0af90a2eabe5.webp?v=1752735797' },
      ],
    },

    // ─── HAIR ──────────────────────────────────────────────────────────────────

    {
      short: 'hair',
      text: 'What about your hair?',
      type: 'multi-select',
      order: 13,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['hair'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'dryness',  text: 'Dryness',                  exclusive: false },
        { short: 'thinning', text: 'Thinning',                  exclusive: false },
        { short: 'loss',     text: 'More than usual hair loss', exclusive: false },
        { short: 'grow',     text: 'Slow growth',               exclusive: false },
        { short: 'none',     text: 'None',                      exclusive: true  },
      ],
    },

    // ─── SKIN ──────────────────────────────────────────────────────────────────

    {
      short: 'skin',
      text: 'What about your skin?',
      type: 'multi-select',
      order: 14,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['skin'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'ageing',     text: 'General ageing',     exclusive: false },
        { short: 'dryness',    text: 'Dryness',             exclusive: false },
        { short: 'elasticity', text: 'Reduced elasticity',  exclusive: false },
        { short: 'acne',       text: 'Breakouts',           exclusive: false },
        { short: 'none',       text: 'None',                exclusive: true  },
      ],
    },

    // ─── DIGESTION ─────────────────────────────────────────────────────────────

    {
      short: 'digestive_health',
      text: 'Do any of these digestive health concerns relate to you?',
      type: 'multi-select',
      order: 15,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['digestion'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'bloating',     text: 'Bloating',          exclusive: false },
        { short: 'constipation', text: 'Constipation',      exclusive: false },
        { short: 'cramping',     text: 'Cramping',          exclusive: false },
        { short: 'diarrhea',     text: 'Diarrhea',          exclusive: false },
        { short: 'reflux',       text: 'Reflux',            exclusive: false },
        { short: 'none',         text: 'None of the above', exclusive: true  },
      ],
    },
    {
      short: 'diarrhea',
      text: 'How often do you experience diarrhea?',
      type: 'single-choice',
      order: 16,
      skipForLoggedIn: false,
      criteria: [{ question: 'digestive_health', options: ['diarrhea'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'occasional',  text: 'Once in a while' },
        { short: 'weekly',      text: 'Once a week' },
        { short: 'frequently',  text: 'More than once a week' },
      ],
    },
    {
      short: 'constipated',
      text: 'How often would you say you are constipated?',
      type: 'single-choice',
      order: 17,
      skipForLoggedIn: false,
      criteria: [{ question: 'digestive_health', options: ['constipation'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'occasional', text: 'Once in a while' },
        { short: 'weekly',     text: 'Once a week' },
        { short: 'frequently', text: 'Frequently' },
      ],
    },

    // ─── ENERGY ────────────────────────────────────────────────────────────────

    {
      short: 'energy_level',
      text: 'Rate your energy levels',
      type: 'single-choice',
      order: 19,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['energy'] }],
      criteriaIsAnd: false,
      options: [
        { short: '1', text: 'Very Low' },
        { short: '2', text: 'Low' },
        { short: '3', text: 'Moderate' },
        { short: '4', text: 'Above Average' },
        { short: '5', text: 'High' },
      ],
    },
    {
      short: 'energy_slumps_experience',
      text: 'Do you often experience energy slumps throughout the day?',
      type: 'single-choice',
      order: 20,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['energy'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'daily', text: 'Yes' },
        { short: 'never', text: 'No' },
      ],
    },

    // ─── STRESS ────────────────────────────────────────────────────────────────

    {
      short: 'stress_level',
      text: 'How stressed have you been in the last month?',
      type: 'single-choice',
      order: 22,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['stress'] }],
      criteriaIsAnd: false,
      options: [
        { short: '1', text: 'Very Low' },
        { short: '2', text: 'Low' },
        { short: '3', text: 'Moderate' },
        { short: '4', text: 'Above Average' },
        { short: '5', text: 'High' },
      ],
    },
    {
      short: 'feel_stressed',
      text: 'How often do you feel stressed?',
      type: 'single-choice',
      order: 23,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['stress'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'less_than_monthly', text: 'Less than once a month' },
        { short: 'monthly',           text: 'Once or twice a month' },
        { short: 'weekly',            text: 'About once or twice per week' },
        { short: 'daily',             text: 'Almost every day' },
      ],
    },

    // ─── SLEEP ─────────────────────────────────────────────────────────────────

    {
      short: 'feeling_sluggish',
      text: 'Do you often wake up in the morning feeling sluggish?',
      type: 'single-choice',
      order: 26,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['sleep'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'daily', text: 'Yes' },
        { short: 'never', text: 'No' },
      ],
    },

    // ─── BRAIN ─────────────────────────────────────────────────────────────────

    {
      short: 'focusing',
      text: 'Do you feel you have trouble focusing or concentrating on tasks?',
      type: 'single-choice',
      order: 28,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['brain'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'daily', text: 'Yes' },
        { short: 'never', text: 'No' },
      ],
    },
    {
      short: 'brain_fog',
      text: 'How often do you experience brain fog?',
      type: 'single-choice',
      order: 29,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['brain'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'daily',  text: 'Almost every day' },
        { short: 'weekly', text: 'Once or twice a week' },
        { short: 'never',  text: 'Never' },
      ],
    },
    {
      short: 'memory',
      text: 'Do you need support with your short term memory?',
      type: 'single-choice',
      order: 29.5,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['brain'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'yes', text: 'Yes' },
        { short: 'no',  text: 'No' },
      ],
    },

    // ─── HEART ─────────────────────────────────────────────────────────────────

    {
      short: 'history_of_heart_problems',
      text: 'Do you have a family history of heart problems?',
      type: 'single-choice',
      order: 30,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['heart'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'yes', text: 'Yes' },
        { short: 'no',  text: 'No' },
      ],
    },
    {
      short: 'heart_concern',
      text: 'Are you concerned about any of the below:',
      type: 'multi-select',
      order: 31,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['heart'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'blood_pressure', text: 'High blood pressure',            exclusive: false },
        { short: 'cholesterol',    text: 'High cholesterol',               exclusive: false },
        { short: 'support',        text: 'General cardiovascular support', exclusive: false },
        { short: 'none',           text: 'None of the above',              exclusive: true  },
      ],
    },
    {
      short: 'coq10',
      text: 'Has your doctor or pharmacist recommended that you take Co-Enzyme Q10 (CoQ10) supplements?',
      type: 'single-choice',
      order: 31.5,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['heart'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'yes', text: 'Yes' },
        { short: 'no',  text: 'No' },
      ],
    },

    // ─── BONES ─────────────────────────────────────────────────────────────────

    {
      short: 'bones',
      text: 'Do you have a family history of bone issues (such as osteoporosis)?',
      type: 'single-choice',
      order: 32,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['bones'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'yes', text: 'Yes' },
        { short: 'no',  text: 'No' },
      ],
    },

    // ─── JOINTS ────────────────────────────────────────────────────────────────

    {
      short: 'joints',
      text: 'Are you concerned about or do you experience any of the below:',
      type: 'multi-select',
      order: 33,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['joints'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'arthritis',  text: 'Mild arthritis', exclusive: false },
        { short: 'joint_pain', text: 'Joint pain',     exclusive: false },
        { short: 'none',       text: 'Neither',        exclusive: true  },
      ],
    },

    // ─── IMMUNITY ──────────────────────────────────────────────────────────────

    {
      short: 'feel_unwell',
      text: 'In relation to colds, flu and infections - how often do you feel unwell each year?',
      type: 'single-choice',
      order: 34,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['immunity'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'more_than_4', text: 'More than 4 times' },
        { short: '2_3_times',   text: '2-3 times' },
        { short: 'rarely',      text: 'Rarely' },
      ],
    },
    {
      short: 'often_get_cold',
      text: 'Do you often get cold sores?',
      type: 'single-choice',
      order: 35,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['immunity'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'yes', text: 'Yes' },
        { short: 'no',  text: 'No' },
      ],
    },
    {
      short: 'antibiotics',
      text: 'Have you taken antibiotics more than twice in the past 3 years?',
      description: 'Antibiotics can cause microbiome disruption.',
      type: 'single-choice',
      order: 35.5,
      skipForLoggedIn: false,
      criteria: [{ question: 'improve_area', options: ['immunity'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'yes', text: 'Yes' },
        { short: 'no',  text: 'No' },
      ],
    },

    // ─── TRANSITION SCREEN ─────────────────────────────────────────────────────

    {
      short: 'diet',
      text: 'Diet',
      type: 'section',
      order: 36,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
    },

    // ─── DIET ──────────────────────────────────────────────────────────────────

    {
      short: 'describe_diets',
      text: 'How would you describe your diet?',
      type: 'single-choice',
      order: 37,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      options: [
        { short: 'any',         text: 'I eat almost everything!' },
        { short: 'plant_based', text: 'Prefer plant-based foods' },
        { short: 'vegetarian',  text: 'Vegetarian' },
        { short: 'vegan',       text: 'Vegan' },
        { short: 'other',       text: 'Other' },
      ],
    },
    {
      // Shown for any/plant_based/other. Skipped for vegetarian and vegan.
      short: 'meat',
      text: 'How often do you eat meat?',
      type: 'single-choice',
      order: 38,
      skipForLoggedIn: false,
      criteria: [{ question: 'describe_diets', options: ['any', 'plant_based', 'other'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'never',        text: 'Never' },
        { short: 'rarely',       text: 'Rarely' },
        { short: '1_2_per_week', text: 'Once/twice per week' },
        { short: '3_per_week',   text: 'Three times per week or more' },
      ],
    },
    {
      // Shown for any/plant_based/other. Skipped for vegetarian and vegan.
      short: 'fish',
      text: 'How often do you eat fish or seafood?',
      type: 'single-choice',
      order: 39,
      skipForLoggedIn: false,
      criteria: [{ question: 'describe_diets', options: ['any', 'plant_based', 'other'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'never',        text: 'Never' },
        { short: 'rarely',       text: 'Rarely' },
        { short: '1_2_per_week', text: 'Once per week' },
        { short: '3_per_week',   text: 'Twice per week or more' },
      ],
    },
    {
      // Shown for any/plant_based/vegetarian/other. Skipped for vegan only.
      short: 'dairy',
      text: 'How often do you eat dairy?',
      type: 'single-choice',
      order: 40,
      skipForLoggedIn: false,
      criteria: [{ question: 'describe_diets', options: ['any', 'plant_based', 'vegetarian', 'other'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'never',        text: 'Never' },
        { short: 'rarely',       text: 'Rarely' },
        { short: '1_2_per_week', text: 'Once/twice per week' },
        { short: '3_per_week',   text: 'Three times per week or more' },
      ],
    },
    {
      short: 'fruit_vegetables',
      text: 'How many serves of fruit and vegetables do you eat daily?',
      description: 'One serve of vegetables equals one cup of salad vegetables, or half a cup of cooked vegetables.',
      type: 'single-choice',
      order: 41,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      options: [
        { short: 'none',          text: 'Almost none' },
        { short: '1_2_serves',    text: '1-2 serves' },
        { short: '3_more_serves', text: '3 serves or more' },
      ],
    },
    {
      short: 'sugar',
      text: 'Do you need support in managing intense sugar cravings throughout the day?',
      type: 'single-choice',
      order: 43,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      options: [
        { short: 'yes', text: 'Yes' },
        { short: 'no',  text: 'No' },
      ],
    },
    {
      short: 'restriction',
      text: 'Do you have any diet restrictions or preferences?',
      type: 'multi-select',
      order: 43.5,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      options: [
        { short: 'limit_dairy', text: 'Limiting dairy', exclusive: false },
        { short: 'gluten_free', text: 'Gluten free',    exclusive: false },
        { short: 'paleo',       text: 'Paleo',          exclusive: false },
        { short: 'none',        text: 'None',           exclusive: true  },
      ],
    },
    {
      short: 'allergy',
      text: 'Are you allergic to any of the following?',
      type: 'multi-select',
      order: 44,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      twoColumn: true,
      options: [
        { short: 'none',      text: 'None',                exclusive: true  },
        { short: 'fish',      text: 'Fish',                exclusive: false },
        { short: 'gluten',    text: 'Wheat and/or Gluten', exclusive: false },
        { short: 'lactose',   text: 'Milk',                exclusive: false },
        { short: 'soy',       text: 'Soy',                 exclusive: false },
        { short: 'sulphites', text: 'Sulphites',            exclusive: false },
        { short: 'yeast',     text: 'Yeast',               exclusive: false },
        { short: 'corn',      text: 'Corn/Maize',          exclusive: false },
        { short: 'treenut',   text: 'Tree nuts',            exclusive: false },
        { short: 'peanut',    text: 'Peanuts',             exclusive: false },
        { short: 'egg',       text: 'Egg',                 exclusive: false },
        { short: 'sesame',    text: 'Sesame',              exclusive: false },
      ],
    },

    // ─── TRANSITION SCREEN ─────────────────────────────────────────────────────

    {
      short: 'lifestyle',
      text: 'Lifestyle',
      type: 'section',
      order: 45,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
    },

    // ─── LIFESTYLE ─────────────────────────────────────────────────────────────

    {
      // NOTE: Option shorts have inconsistent separator style inherited from Octane.
      // '1-day' uses a hyphen; '2_3_days' and '4_plus_days' use underscores.
      // Do NOT normalise — verify backend recommendation logic first.
      short: 'exercise_days',
      text: 'How many days per week do you exercise on average?',
      type: 'single-choice',
      order: 46,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      options: [
        { short: 'none',        text: "I don't exercise" },
        { short: '1-day',       text: '1' },
        { short: '2_3_days',    text: '2-3' },
        { short: '4_plus_days', text: '4 or more' },
      ],
    },
    {
      short: 'exercise_type',
      text: 'What type of exercise do you engage in?',
      type: 'multi-select',
      order: 47,
      skipForLoggedIn: false,
      criteria: [{ question: 'exercise_days', options: ['1-day', '2_3_days', '4_plus_days'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'cardio',    text: 'Cardio (running, cycling, HIIT)',     exclusive: false },
        { short: 'core_yoga', text: 'Core and stretching (Yoga, Pilates)', exclusive: false },
        { short: 'walking',   text: 'Walking, hiking',                     exclusive: false },
        { short: 'weights',   text: 'Weightlifting, strength training',    exclusive: false },
        { short: 'other',     text: 'Other',                               exclusive: false },
      ],
    },
    {
      short: 'exercise_intensity_rating',
      text: 'How would you rate the intensity of the exercise you engage in?',
      type: 'single-choice',
      order: 48,
      skipForLoggedIn: false,
      criteria: [{ question: 'exercise_days', options: ['1-day', '2_3_days', '4_plus_days'] }],
      criteriaIsAnd: false,
      options: [
        { short: '1', text: 'Very Low' },
        { short: '2', text: 'Low' },
        { short: '3', text: 'Moderate' },
        { short: '4', text: 'Above Average' },
        { short: '5', text: 'High' },
      ],
    },
    {
      short: 'fitness_focus',
      text: 'When it comes to exercise, you care most about:',
      type: 'multi-select',
      order: 49,
      skipForLoggedIn: false,
      criteria: [{ question: 'exercise_days', options: ['1-day', '2_3_days', '4_plus_days'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'performance',     text: 'Performance',     exclusive: false },
        { short: 'breaking_sweat',  text: 'Breaking a sweat',exclusive: false },
        { short: 'muscle_toning',   text: 'Muscle Toning',   exclusive: false },
        { short: 'muscle_building', text: 'Muscle Building', exclusive: false },
      ],
    },
    {
      short: 'muscle_cramps',
      text: 'Do you sometimes get muscle cramps, tightness, or soreness after being active or exercising?',
      type: 'single-choice',
      order: 50,
      skipForLoggedIn: false,
      criteria: [{ question: 'exercise_days', options: ['1-day', '2_3_days', '4_plus_days'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'yes', text: 'Yes' },
        { short: 'no',  text: 'No' },
      ],
    },
    {
      short: 'sunshine',
      text: 'How often do you get 20 min of sunshine (at least on your face and hands, without sunscreen)?',
      type: 'single-choice',
      order: 51,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      options: [
        { short: 'rarely',   text: "Rarely, I don't really get in the sun" },
        { short: 'weekends', text: 'On weekends and holidays only' },
        { short: 'daily',    text: 'Every day!' },
      ],
    },
    {
      short: 'alcoholic_drink_a_night',
      text: 'Do you often consume 8 or more alcoholic drinks in a week?',
      description: 'Alcohol can impact nutrient absorption and energy levels.',
      type: 'single-choice',
      order: 53,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      options: [
        { short: 'daily', text: 'Yes' },
        { short: 'never', text: 'No' },
      ],
    },
    {
      short: 'smoke',
      text: 'Do you smoke or vape regularly?',
      description: 'Smoking and vaping can lower your body\u2019s levels of key vitamins like vitamin C and antioxidants.',
      type: 'single-choice',
      order: 54,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      options: [
        { short: 'frequently', text: 'Yes' },
        { short: 'no',         text: 'No' },
      ],
    },
    {
      short: 'urinary_tract_health',
      text: 'Do you feel you need support with your urinary tract health?',
      type: 'single-choice',
      order: 55,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      options: [
        { short: 'yes', text: 'Yes' },
        { short: 'no',  text: 'No' },
      ],
    },
    {
      // Shown for male gender only
      short: 'sexual_health',
      text: 'Are you looking at improving your sperm health?',
      type: 'single-choice',
      order: 55.4,
      skipForLoggedIn: false,
      criteria: [{ question: 'gender', options: ['male'] }],
      criteriaIsAnd: false,
      options: [
        { short: 'yes', text: 'Yes' },
        { short: 'no',  text: 'No' },
      ],
    },
    {
      short: 'eyes',
      text: 'Do you experience visual fatigue at the end of the day from looking at screens?',
      type: 'single-choice',
      order: 55.49,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      options: [
        { short: 'yes', text: 'Yes' },
        { short: 'no',  text: 'No' },
      ],
    },
    {
      short: 'iron',
      text: 'Has your doctor recommended that you take iron?',
      type: 'single-choice',
      order: 55.5,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      options: [
        { short: 'yes', text: 'Yes' },
        { short: 'no',  text: 'No' },
      ],
    },
    {
      short: 'traditional',
      text: 'In regards to traditional Eastern Medicines like Ayurveda, you:',
      type: 'single-choice',
      order: 55.6,
      skipForLoggedIn: false,
      criteria: [],
      criteriaIsAnd: false,
      options: [
        { short: 'believer',  text: 'Believe that there is wisdom in alternative medicines.' },
        { short: 'open',      text: 'Are unsure and would like more information.' },
        { short: 'skeptical', text: "Are skeptical and don't believe in alternative medicines." },
      ],
    },

    // ─── FINAL — EMAIL ────────────────────────────────────────────────────────
    // Email is the last question before submission. Guests only — logged-in
    // users skip this because their email comes from __vitableCustomer.

    {
      short: 'email',
      text: "What's your email address?",
      description: 'So we can save your answers.',
      type: 'text-input',
      order: 99,
      skipForLoggedIn: true,
      criteria: [],
      criteriaIsAnd: false,
      validation: { email: true },
      privacyPolicyUrl: 'https://vitable.com.au/policies/privacy-policy',
    },

  ],
};
